import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import {
  AccountDetailsHeader,
  BalancesHeader,
  CurrentDueContainer,
  CurrentDueData,
  CurrentDueHeaderMobile,
  DueDateContainer,
  DueDateData,
  DueDateHeaderMobile,
  PayoffAmountData,
  PayoffAmountHeaderMobile,
  PayoffContainer,
  PayoffCurrentDueAndDueDateContainer,
  RecentPaymentsHeader,
  UnderlyingPoliciesWarning,
} from './styles';

import AccountRows from './accountRows';
import RecentPaymentsRow from './recentPaymentsRow';
import ScheduledPendingPaymentsRow from './scheduledPendingPaymentsRow';
import StatementAccountRow from './statementAccountRow';
import UnderlyingAccountRows from './underlyingAccountRows';
import { getPaymentTypes } from '../dashboard/billing/billingAPI';
import { useLocation } from 'react-router-dom';
import { resetStepTwoState } from '../make-payment/step-two-amount/makePaymentStepTwoReducer';
import { getText } from '../../../utils/i18n';
import { setAccountToPayWith } from '../make-payment/makePaymentReducer';

export const AccountDetails = (props) => {
  const {
    billingAccounts,
    dispatch,
    i18n,
    paymentTypes,
    pendingOrScheduledPayments,
    recentPayments,
    underlyingPoliciesPayPlansEditable,
  } = props;
  let {
    state: { accountNumber },
  } = useLocation();
  const account = billingAccounts.find((account) => account.fullAccount.systemId === accountNumber);

  let initialState = account?.fullAccount.underlyingBillingAccounts
    ? account?.fullAccount?.underlyingBillingAccounts.reduce((result, thisaccount) => {
        if (thisaccount.accountDisplayName === 'Statement') {
          // we have to replace the statement account id with its account id because this changes on save
          result[account.fullAccount.id] = false;
        } else {
          result[thisaccount.id] = false;
        }
        return result;
      }, {})
    : { [account?.fullAccount.id]: false };

  let [showPaymentSchedule, setShowPaymentSchedule] = useState(initialState);
  let [showEditPaymentSchedule, setShowEditPaymentSchedule] = useState(initialState);
  let [showEditPaymentMethod, setShowEditPaymentMethod] = useState(initialState);
  let [policyToUpdatePayPlan, setPolicyToUpdatePayPlan] = useState(null);
  useEffect(() => {
    dispatch(getPaymentTypes());
    window.scrollTo(0, 0);
    const account = billingAccounts.find(
      (account) => account.fullAccount.systemId === accountNumber,
    );
    dispatch(setAccountToPayWith(account));
    return () => {
      // reset pay instruments for account details if a users leaves the details page
      // and unlisten
      dispatch(resetStepTwoState(null));
    };
  }, []);

  const isStatementAccountCheck = () => {
    const account = billingAccounts.find(
      (account) => account.fullAccount.systemId === accountNumber,
    );
    return account.fullAccount.accountDisplayName === 'Statement';
  };
  const checkIfUnderlyingAccountsAreAvailable = () => {
    const account = billingAccounts.find(
      (account) => account.fullAccount.systemId === accountNumber,
    );

    return (
      account.fullAccount.accountDisplayName === 'Statement' &&
      account.fullAccount.underlyingBillingAccounts.length > 1
    );
  };
  return (
    <>
      <Helmet>
        <title>Account Details</title>
      </Helmet>
      {accountNumber ? (
        <>
          {billingAccounts.map((account) => {
            const { fullAccount } = account;
            if (fullAccount.systemId === accountNumber) {
              return (
                <React.Fragment key={fullAccount.id}>
                  <AccountDetailsHeader>
                    Account {fullAccount.accountDisplayNumber}
                  </AccountDetailsHeader>
                  <PayoffCurrentDueAndDueDateContainer>
                    <PayoffContainer>
                      <PayoffAmountHeaderMobile>Payoff Amount</PayoffAmountHeaderMobile>
                      <PayoffAmountData>
                        ${parseFloat(fullAccount.totalAmt).toFixed(2)}
                      </PayoffAmountData>
                    </PayoffContainer>
                    <CurrentDueContainer>
                      <CurrentDueHeaderMobile>Current Due</CurrentDueHeaderMobile>
                      <CurrentDueData>${parseFloat(fullAccount.openAmt).toFixed(2)}</CurrentDueData>
                    </CurrentDueContainer>
                    <DueDateContainer>
                      <DueDateHeaderMobile>Due Date</DueDateHeaderMobile>
                      <DueDateData>{account.dueDate}</DueDateData>
                    </DueDateContainer>
                  </PayoffCurrentDueAndDueDateContainer>
                  {recentPayments && (
                    <>
                      <RecentPaymentsHeader>Recent Payments</RecentPaymentsHeader>
                      <RecentPaymentsRow fullAccount={fullAccount} paymentTypes={paymentTypes} />
                    </>
                  )}
                  {pendingOrScheduledPayments && (
                    <>
                      <RecentPaymentsHeader>Pending/Scheduled Payments</RecentPaymentsHeader>
                      <ScheduledPendingPaymentsRow
                        fullAccount={fullAccount}
                        paymentTypes={paymentTypes}
                      />
                    </>
                  )}
                  <BalancesHeader>Balances </BalancesHeader>
                  {isStatementAccountCheck() ? (
                    <>
                      {fullAccount.payPlan.schedule ? (
                        <StatementAccountRow
                          account={account}
                          accountNumber={accountNumber}
                          initialState={initialState}
                          showPaymentSchedule={showPaymentSchedule}
                          setShowPaymentSchedule={setShowPaymentSchedule}
                          setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                          showEditPaymentSchedule={showEditPaymentSchedule}
                          showEditPaymentMethod={showEditPaymentMethod}
                          setShowEditPaymentMethod={setShowEditPaymentMethod}
                        />
                      ) : (
                        <UnderlyingPoliciesWarning
                          dangerouslySetInnerHTML={{
                            __html: getText(
                              i18n,
                              'changePayPlan.noUnderlyingPayPlan',
                              'There are no unbilled schedule(s) found. Please contact your agent if you have questions.',
                            ),
                          }}
                        ></UnderlyingPoliciesWarning>
                      )}
                    </>
                  ) : (
                    <AccountRows
                      fullAccount={fullAccount}
                      initialState={initialState}
                      setShowPaymentSchedule={setShowPaymentSchedule}
                      showPaymentSchedule={showPaymentSchedule}
                      setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                      showEditPaymentSchedule={showEditPaymentSchedule}
                      showEditPaymentMethod={showEditPaymentMethod}
                      setShowEditPaymentMethod={setShowEditPaymentMethod}
                      policyToUpdatePayPlan={policyToUpdatePayPlan}
                      setPolicyToUpdatePayPlan={setPolicyToUpdatePayPlan}
                    />
                  )}
                  {checkIfUnderlyingAccountsAreAvailable() && (
                    <>
                      {underlyingPoliciesPayPlansEditable && (
                        <UnderlyingPoliciesWarning
                          dangerouslySetInnerHTML={{
                            __html: getText(
                              i18n,
                              'changePayPlan.underlyingPayPlanWarning',
                              'Please Note: editing individual underlying policies will remove them from the current Statement Account',
                            ),
                          }}
                        ></UnderlyingPoliciesWarning>
                      )}
                      <UnderlyingAccountRows
                        fullAccount={fullAccount}
                        initialState={initialState}
                        isUnderlyingPolicies={true}
                        setShowPaymentSchedule={setShowPaymentSchedule}
                        showPaymentSchedule={showPaymentSchedule}
                        setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                        showEditPaymentSchedule={showEditPaymentSchedule}
                        showEditPaymentMethod={showEditPaymentMethod}
                        setShowEditPaymentMethod={setShowEditPaymentMethod}
                        policyToUpdatePayPlan={policyToUpdatePayPlan}
                        setPolicyToUpdatePayPlan={setPolicyToUpdatePayPlan}
                      />
                    </>
                  )}
                </React.Fragment>
              );
            } else {
              return null;
            }
          })}
        </>
      ) : (
        <div>accounts unavailable</div>
      )}
    </>
  );
};
AccountDetails.propTypes = {
  i18n: PropTypes.any,
  billingAccounts: PropTypes.array,
  dispatch: PropTypes.func,
  location: PropTypes.shape({
    state: PropTypes.shape({
      accountNumber: PropTypes.string,
      policyToUpdatePayPlan: PropTypes.any,
    }),
  }),
  paymentTypes: PropTypes.object,
  pendingOrScheduledPayments: PropTypes.bool,
  recentPayments: PropTypes.bool,
  underlyingPoliciesPayPlansEditable: PropTypes.bool,
};
export const mapStateToProps = (state) => ({
  i18n: state.i18nSlice.i18n,
  billingAccounts: state.billingSlice.billingAccounts,
  paymentTypes: state.billingSlice.paymentTypes,
  pendingOrScheduledPayments:
    state.configurationSlice.features.features.pendingOrScheduledPaymentsList,
  recentPayments: state.configurationSlice.features.features.recentPaymentsList,
  underlyingPoliciesPayPlansEditable:
    state.configurationSlice.features.features.underlyingPoliciesPayPlansEditable,
});

export default connect(mapStateToProps)(AccountDetails);
